import { createContext, useCallback, useContext, useRef, useState } from 'react';

async function fetchRandomTrack() {
  const res = await fetch('/api/tracks/random');
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

const MusicPlayerContext = createContext(null);

export function MusicPlayerProvider({ children }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const audioRef = useRef(null);
  const queueRef = useRef([]);

  const preFetchNext = useCallback(() => {
    fetchRandomTrack()
      .then((track) => {
        if (track?.streamUrl) queueRef.current.push(track);
      })
      .catch(() => {});
  }, []);

  const playFromQueue = useCallback(() => {
    const next = queueRef.current.shift();
    if (!next?.streamUrl) {
      setIsPlaying(false);
      return;
    }
    const audio = audioRef.current;
    if (!audio) return;
    setCurrentTrack(next);
    audio.src = next.streamUrl;
    audio.play();
    preFetchNext();
  }, [preFetchNext]);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.pause();
      audio.src = '';
    }
    queueRef.current = [];
    setIsPlaying(false);
  }, []);

  const fetchAndPlay = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const track = await fetchRandomTrack();
      const streamUrl = track.streamUrl;
      if (!streamUrl) throw new Error('No stream URL');
      const audio = audioRef.current || new Audio();
      audioRef.current = audio;
      audio.src = streamUrl;
      audio.onended = () => playFromQueue();
      audio.onerror = () => {
        setError('Playback failed');
        playFromQueue();
      };
      setCurrentTrack(track);
      await audio.play();
      setIsPlaying(true);
      preFetchNext();
    } catch (e) {
      setError(e.message || 'Failed to load track');
      setIsPlaying(false);
    } finally {
      setLoading(false);
    }
  }, [preFetchNext, playFromQueue]);

  const handlePlayStop = useCallback(() => {
    if (isPlaying) {
      stop();
    } else {
      fetchAndPlay();
    }
  }, [isPlaying, stop, fetchAndPlay]);

  const handlePrev = useCallback(() => fetchAndPlay(), [fetchAndPlay]);
  const handleNext = useCallback(() => fetchAndPlay(), [fetchAndPlay]);

  const openTrackInTab = useCallback(() => {
    const track = currentTrack;
    if (track?.permalink) {
      window.open(`https://audius.co${track.permalink.startsWith('/') ? '' : '/'}${track.permalink}`, '_blank');
    }
  }, [currentTrack]);

  const value = {
    isPlaying,
    currentTrack,
    loading,
    error,
    stop,
    handlePlayStop,
    handlePrev,
    handleNext,
    openTrackInTab,
  };

  return (
    <MusicPlayerContext.Provider value={value}>
      {children}
    </MusicPlayerContext.Provider>
  );
}

export function useMusicPlayer() {
  const ctx = useContext(MusicPlayerContext);
  if (!ctx) throw new Error('useMusicPlayer must be used within MusicPlayerProvider');
  return ctx;
}
